// Auth utility for storing and retrieving tokens and user data

const DOCTOR_TOKEN_KEY = 'doctor_access_token';
const DOCTOR_TOKEN_EXPIRY_KEY = 'doctor_token_expiry';
const DOCTOR_USER_KEY = 'doctor_user';

const HOSPITAL_TOKEN_KEY = 'hospital_access_token';
const HOSPITAL_TOKEN_EXPIRY_KEY = 'hospital_token_expiry';
const HOSPITAL_USER_KEY = 'hospital_user';

export interface DoctorUser {
  id: string;
  fullName: string;
  email: string;
  specialization: string;
  clinicName: string;
  clinicAddress: string;
  phoneNumber: string;
  licenseNumber: string;
  createdAt: string;
}

export interface HospitalUser {
  id: string;
  hospitalName: string;
  branchName: string;
  branchAddress: string;
  licenseNumber: string;
  contactPersonName: string;
  contactEmail: string;
  contactPhone: string;
  status: string;
  createdAt: string;
}

export interface StaffUser {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  department?: string;
  role: string;
  status: string;
  isFirstLogin?: boolean;
  hospitalId: string;
  createdAt: string;
  permissions?: Array<{
    id: string;
    staffId: string;
    module: string;
    canView: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    canApprove: boolean;
    canUpload: boolean;
    createdAt: string;
    updatedAt: string;
  }> | any[];
}


export const auth = {
  // ================= DOCTOR =================

  setDoctorAuth: (token: string, expiresIn: number | string, user: DoctorUser) => {
    if (typeof window !== 'undefined') {
      const validExpiresIn = Number(expiresIn) || 3600;
      localStorage.setItem(DOCTOR_TOKEN_KEY, token);
      localStorage.setItem(
        DOCTOR_TOKEN_EXPIRY_KEY,
        (Date.now() + validExpiresIn * 1000).toString()
      );
      localStorage.setItem(DOCTOR_USER_KEY, JSON.stringify(user));
    }
  },

  getDoctorToken: (): string | null => {
    if (typeof window === 'undefined') return null;

    const token = localStorage.getItem(DOCTOR_TOKEN_KEY);
    const expiry = localStorage.getItem(DOCTOR_TOKEN_EXPIRY_KEY);

    if (!token || !expiry) return null;

    if (Date.now() > parseInt(expiry)) {
      auth.clearDoctorAuth();
      return null;
    }

    return token;
  },

  getDoctorUser: (): DoctorUser | null => {
    if (typeof window === 'undefined') return null;

    const userStr = localStorage.getItem(DOCTOR_USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  },

  clearDoctorAuth: () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(DOCTOR_TOKEN_KEY);
      localStorage.removeItem(DOCTOR_TOKEN_EXPIRY_KEY);
      localStorage.removeItem(DOCTOR_USER_KEY);
    }
  },

  isDoctorAuthenticated: (): boolean => {
    return auth.getDoctorToken() !== null;
  },

  // ================= HOSPITAL =================

setHospitalAuth: (token: string, expiresIn: number | string, user: StaffUser) => {
    if (typeof window !== 'undefined') {
      const validExpiresIn = Number(expiresIn) || 3600; // Fix NaN expiry
      if (isNaN(validExpiresIn)) {
        throw new Error('Invalid token expiry');
      }
      localStorage.setItem(HOSPITAL_TOKEN_KEY, token);
      localStorage.setItem(
        HOSPITAL_TOKEN_EXPIRY_KEY,
        (Date.now() + validExpiresIn * 1000).toString()
      );
      localStorage.setItem(HOSPITAL_USER_KEY, JSON.stringify(user));
    }
  },


  getHospitalToken: (): string | null => {
    if (typeof window === 'undefined') return null;

    const token = localStorage.getItem(HOSPITAL_TOKEN_KEY);
    const expiry = localStorage.getItem(HOSPITAL_TOKEN_EXPIRY_KEY);

    if (!token || !expiry) return null;

    if (Date.now() > parseInt(expiry)) {
      auth.clearHospitalAuth();
      return null;
    }

    return token;
  },

  getHospitalUser: (): StaffUser | null => {
    if (typeof window === 'undefined') return null;

    const userStr = localStorage.getItem(HOSPITAL_USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  },

  clearHospitalAuth: () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(HOSPITAL_TOKEN_KEY);
      localStorage.removeItem(HOSPITAL_TOKEN_EXPIRY_KEY);
      localStorage.removeItem(HOSPITAL_USER_KEY);
    }
  },

  isHospitalAuthenticated: (): boolean => {
    return auth.getHospitalToken() !== null;
  },

  // ================= LOGIN =================

  loginAsDoctor: (token: string, expiresIn: number, user: DoctorUser) => {
    auth.setDoctorAuth(token, expiresIn, user);
    return { role: 'doctor' as const, user };
  },

  loginAsHospital: (token: string, expiresIn: number, user: StaffUser) => {
    auth.setHospitalAuth(token, expiresIn, user);
    return { role: 'hospital' as const, user };
  },

  // ================= ROLE (PATH BASED) =================

  getRoleFromPath: (pathname: string): 'doctor' | 'hospital' | null => {
    if (pathname.startsWith('/doctor')) return 'doctor';
    if (pathname.startsWith('/hospital')) return 'hospital';
    return null;
  },

  // ================= TOKEN (MAIN FIX) =================

  getToken: (pathname: string): string | null => {
    const role = auth.getRoleFromPath(pathname);

    if (role === 'doctor') return auth.getDoctorToken();
    if (role === 'hospital') return auth.getHospitalToken();

    return null;
  },

  // ================= USER =================

  getUser: (pathname: string): DoctorUser | StaffUser | null => {
    const role = auth.getRoleFromPath(pathname);

    if (role === 'doctor') return auth.getDoctorUser();
    if (role === 'hospital') return auth.getHospitalUser();

    return null;
  },

  // ================= LOGOUT =================

  clearDoctor: () => auth.clearDoctorAuth(),
  clearHospital: () => auth.clearHospitalAuth(),

  clearAll: () => {
    auth.clearDoctorAuth();
    auth.clearHospitalAuth();
  },
};

export default auth;